import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCamera, FiShare2, FiHeart, FiShield, FiPalette, FiClock } = FiIcons;

function Home() {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-rose-50 pt-16">
      {/* Hero Section */}
      <motion.section 
        className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        {/* Organic Background Shape */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-teal-200 to-cyan-300 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-rose-200 to-pink-300 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-to-br from-cyan-200 to-teal-300 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          {/* Logo */}
          <motion.div className="mb-8" variants={fadeInUp}>
            <div className="inline-flex items-center space-x-3 text-5xl md:text-7xl font-black">
              <span className="text-gray-900 font-black tracking-tight" style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif', fontWeight: 900, letterSpacing: '-0.02em' }}>2024</span>
            </div>
          </motion.div>

          {/* Headline */}
          <motion.h1 
            className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight"
            variants={fadeInUp}
          >
            Your<br />
            <span className="bg-gradient-to-r from-teal-500 via-cyan-500 to-rose-500 bg-clip-text text-transparent">Event Memories</span>
          </motion.h1>

          {/* Subtext */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-600 mb-12 font-light max-w-2xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            Capture, share, and keep your event moments forever
          </motion.p>

          {/* CTA Button */}
          <motion.div variants={fadeInUp}>
            <a 
              href="https://wa.me/919059468240" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600 text-white px-12 py-4 rounded-full text-lg font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 transform inline-block"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start My Gallery
            </a>
          </motion.div>
        </div>
      </motion.section>

      {/* How It Works Section */}
      <motion.section 
        className="py-20 px-4"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16"
            variants={fadeInUp}
          >
            Simple & Effortless
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiCamera,
                step: "01",
                title: "Make your gallery in minutes",
                description: "Set up your event gallery with just a few clicks. No technical skills needed.",
                gradient: "from-teal-400 to-cyan-500"
              },
              {
                icon: FiShare2,
                step: "02",
                title: "Share the link with your guests",
                description: "Send one simple link to all your guests via text, email, or social media.",
                gradient: "from-cyan-400 to-blue-500"
              },
              {
                icon: FiHeart,
                step: "03",
                title: "Everyone uploads, everyone remembers",
                description: "Guests instantly upload photos and videos. Everyone gets access to all memories.",
                gradient: "from-blue-400 to-rose-500"
              }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                className="text-center group"
                variants={fadeInUp}
              >
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:scale-105 border border-gray-100">
                  <div className={`bg-gradient-to-r ${item.gradient} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <SafeIcon icon={item.icon} className="text-2xl text-white" />
                  </div>
                  <div className="text-sm font-bold text-gray-400 mb-2">STEP {item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Why Choose 2024 Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-gray-50 to-white"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16"
            variants={fadeInUp}
          >
            Why Choose 2024?
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: FiShield,
                title: "Private & Safe",
                description: "Only your guests",
                detail: "Your memories stay secure with invite-only access and privacy controls.",
                gradient: "from-teal-400 to-cyan-500",
                bgGradient: "from-teal-50 to-cyan-50"
              },
              {
                icon: FiPalette,
                title: "Custom Look",
                description: "Your event, your style",
                detail: "Personalize your gallery with themes, colors, and branding that match your event.",
                gradient: "from-cyan-400 to-blue-500",
                bgGradient: "from-cyan-50 to-blue-50"
              },
              {
                icon: FiClock,
                title: "Forever Memories",
                description: "No expiry",
                detail: "Your photos and videos are stored safely forever. No time limits, no deletions.",
                gradient: "from-blue-400 to-rose-500",
                bgGradient: "from-blue-50 to-rose-50"
              }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                className="text-center"
                variants={fadeInUp}
              >
                <div className={`bg-gradient-to-br ${item.bgGradient} rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-white/50`}>
                  <div className={`bg-gradient-to-r ${item.gradient} w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                    <SafeIcon icon={item.icon} className="text-3xl text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-lg font-semibold text-gray-700 mb-4">{item.description}</p>
                  <p className="text-gray-600 leading-relaxed">{item.detail}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Emotional Quote Section */}
      <motion.section 
        className="py-32 px-4 relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-teal-500 to-rose-500"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.blockquote 
            className="text-3xl md:text-5xl font-light text-white leading-relaxed"
            variants={fadeInUp}
          >
            "Because memories fade,<br />
            <span className="font-semibold bg-gradient-to-r from-teal-300 via-cyan-300 to-rose-300 bg-clip-text text-transparent"> but your story can stay forever."</span>
          </motion.blockquote>
        </div>
      </motion.section>

      {/* Final CTA Section */}
      <motion.section 
        className="py-20 px-4 bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-600"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl md:text-6xl font-bold text-white mb-8"
            variants={fadeInUp}
          >
            Make Your Event <br /> Infinite
          </motion.h2>
          <motion.div variants={fadeInUp}>
            <a 
              href="https://wa.me/919059468240" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-gray-900 px-12 py-4 rounded-full text-lg font-semibold shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 transform inline-block"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Create My Gallery
            </a>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-center">
        <div className="text-gray-400">
          <p>&copy; 2024 - Your Infinite Memories</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;